import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Camera preview and frame capture for the guided photo.
 * Prefers the rear camera, allows switching when more than one is available
 * and always stops the stream on unmount.
 */

const CAPTURE_QUALITY = 0.95;

const BASE_VIDEO_CONSTRAINTS = {
  width: { ideal: 1920 },
  height: { ideal: 1080 },
};

const CAMERA_ERRORS = {
  INSECURE_CONTEXT: "A câmera só funciona em conexão segura (https). Envie uma foto da galeria.",
  UNSUPPORTED: "Este navegador não permite usar a câmera. Envie uma foto da galeria.",
  PERMISSION_DENIED: "O acesso à câmera foi negado. Libere a permissão nas configurações do navegador ou envie uma foto da galeria.",
  NO_CAMERA: "Nenhuma câmera foi encontrada neste dispositivo. Envie uma foto da galeria.",
  CAMERA_BUSY: "A câmera está sendo usada por outro aplicativo. Feche-o e tente novamente, ou envie uma foto da galeria.",
  UNKNOWN: "Não foi possível abrir a câmera. Tente novamente ou envie uma foto da galeria.",
  CAPTURE_FAILED: "Não foi possível capturar a foto. Tente novamente.",
};

const cameraError = (code) => ({ code, message: CAMERA_ERRORS[code] || CAMERA_ERRORS.UNKNOWN });

const mapMediaError = (error) => {
  switch (error && error.name) {
    case "NotAllowedError":
    case "SecurityError":
      return cameraError("PERMISSION_DENIED");
    case "NotFoundError":
    case "OverconstrainedError":
      return cameraError("NO_CAMERA");
    case "NotReadableError":
    case "AbortError":
      return cameraError("CAMERA_BUSY");
    default:
      return cameraError("UNKNOWN");
  }
};

export const isCameraSupported = () =>
  typeof navigator !== "undefined" && Boolean(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

const useCameraCapture = () => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const devicesRef = useRef([]);
  const deviceIdRef = useRef(null);

  const [status, setStatus] = useState("idle"); // idle | starting | active | error
  const [error, setError] = useState(null);
  const [canSwitch, setCanSwitch] = useState(false);
  const [videoSize, setVideoSize] = useState(null);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    setStatus("idle");
  }, []);

  const refreshDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      devicesRef.current = devices.filter((device) => device.kind === "videoinput");
      setCanSwitch(devicesRef.current.length > 1);
    } catch (e) {
      setCanSwitch(false);
    }
  }, []);

  const start = useCallback(async (deviceId) => {
    if (typeof window !== "undefined" && window.isSecureContext === false) {
      setError(cameraError("INSECURE_CONTEXT"));
      setStatus("error");
      return;
    }
    if (!isCameraSupported()) {
      setError(cameraError("UNSUPPORTED"));
      setStatus("error");
      return;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    setStatus("starting");
    setError(null);

    const video = deviceId
      ? { ...BASE_VIDEO_CONSTRAINTS, deviceId: { exact: deviceId } }
      : { ...BASE_VIDEO_CONSTRAINTS, facingMode: { ideal: "environment" } };

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video, audio: false });
      streamRef.current = stream;

      const track = stream.getVideoTracks()[0];
      const settings = track && track.getSettings ? track.getSettings() : {};
      deviceIdRef.current = settings.deviceId || deviceId || null;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }

      setStatus("active");
      // Device labels and the full list are only reliable after permission is granted.
      refreshDevices();
    } catch (e) {
      setError(mapMediaError(e));
      setStatus("error");
    }
  }, [refreshDevices]);

  const switchCamera = useCallback(() => {
    const devices = devicesRef.current;
    if (devices.length < 2) return;

    const currentIndex = devices.findIndex((device) => device.deviceId === deviceIdRef.current);
    const next = devices[(currentIndex + 1) % devices.length];
    start(next.deviceId);
  }, [start]);

  /** Captures the current frame at the stream resolution as a JPEG Blob. */
  const capture = useCallback(() => new Promise((resolve, reject) => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      reject(cameraError("CAPTURE_FAILED"));
      return;
    }

    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(cameraError("CAPTURE_FAILED"))),
      "image/jpeg",
      CAPTURE_QUALITY
    );
  }), []);

  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video && video.videoWidth && video.videoHeight) {
      setVideoSize({ width: video.videoWidth, height: video.videoHeight });
    }
  }, []);

  useEffect(() => () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  }, []);

  return {
    videoRef,
    status,
    error,
    canSwitch,
    videoSize,
    start,
    stop,
    switchCamera,
    capture,
    handleLoadedMetadata,
  };
};

export default useCameraCapture;
